import { forwardRef, useState, useEffect } from "react";
import {
  Badge,
  Center,
  Flex,
  Loader,
  Tabs,
  Text,
  useMantineTheme,
} from "@mantine/core";
import CardShopping from "./CardProduct";
import { IQueryMyProducts } from "../../../interfaces/IQuery";
import { useMyProducts } from "../../../hooks/useMyProducts";
import { useTranslation } from "react-i18next";
import { Select } from "../../../components";
import { useMediaQuery } from "@mantine/hooks";
import i18n from "../../../utils/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { EServiceType } from "../../../interfaces/MyPlans/IMyPlans";

interface IViews {
  total: number;
  active: number;
  inactive: number;
}
interface IContentTabs extends React.ComponentPropsWithoutRef<"div"> {
  id: string;
  label: string;
  badgeNumber: number;
  color: string;
  value: EServiceType | "";
  disabled: boolean;
}

const MyProducts = () => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const isLargerThan991 = useMediaQuery("(min-width: 991px)");
  const languageSelected = i18n.language;
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<string | null>(
    t("meu_portfolio.meus_serviços.all_products")
  );
  const [query, setQuery] = useState<IQueryMyProducts>({
    active: EServiceType.All,
  });

  const [views, setViews] = useState<IViews>({
    total: 0,
    active: 0,
    inactive: 0,
  });

  const { total, active, inactive } = views;
  const { data, isLoading } = useMyProducts(query);
  useEffect(() => {
    const language = localStorage.getItem("i18nextLng");
    if (languageSelected !== language)
      queryClient.invalidateQueries({
        queryKey: ["MyPlans", query.active],
        exact: false,
      });
  }, [languageSelected]);

  useEffect(() => {
    if (!!data?.views) {
      if (activeTab === EServiceType.true && data?.views.active === 0) {
        setQuery((prev) => ({ ...prev, status: "" }));
        setActiveTab(EServiceType.All);
      }
      setViews(data.views);
    }
  }, [data, activeTab, active]);

  const contentTabs: IContentTabs[] = [
    {
      id: "1",
      label: t("meu_portfolio.meus_serviços.all_products"),
      badgeNumber: data?.views?.total || 0,
      color: "primary.1",
      value: EServiceType.All,
      disabled: total === 0,
    },
    {
      id: "2",
      label: t("meu_portfolio.meus_serviços.active_products"),
      badgeNumber: data?.views?.active || 0,
      color: "alerts.2",
      value: EServiceType.true,
      disabled: active === 0,
    },
    {
      id: "3",
      label: t("meu_portfolio.meus_serviços.inactive_products"),
      badgeNumber: data?.views?.inactive || 0,
      color: "alerts.1",
      value: EServiceType.false,
      disabled: inactive === 0,
    },
  ];
  const handleTabChange = (status: EServiceType | "") => {
    setQuery((prev) => ({
      ...prev,
      status: EServiceType.All === status ? "" : status,
    }));
  };

  const badgeNumber = contentTabs.find(
    (tab) => tab.value === query.active
  )?.badgeNumber;

  const SelectItem = forwardRef<HTMLDivElement, IContentTabs>(
    (
      { label, value, badgeNumber, color, disabled, ...others }: IContentTabs,
      ref
    ) => {
      return (
        <div
          ref={ref}
          {...others}
          style={{
            filter: disabled ? "opacity(0.5)" : "unset",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <Flex key={value} gap="8px">
            <Badge
              color={color}
              h={22}
              styles={{
                root: {
                  fontWeight: 600,
                  fontSize: "14px",
                },
              }}
              variant="filled"
              px={8}
            >
              {badgeNumber >= 10 ? badgeNumber : "0" + badgeNumber}
            </Badge>
            <Text
              c="basic.4"
              fw={{ base: 600 }}
              fz={{ base: 16 }}
              lh={{ base: "24.8px" }}
            >
              {label}
            </Text>
          </Flex>
        </div>
      );
    }
  );
  return (
    <Center bg="other.0">
      <Flex
        direction="column"
        w="100%"
        maw={1680}
        px={{ base: 24, 768: 60 }}
        pb={96}
      >
        <Flex justify={"space-between"} align="center" mt={32} mb={20}>
          <Text
            fz={{ base: 32 }}
            fw={{ base: 700 }}
            c="basic.5"
            lh={{ base: "155%" }}
          >
            {t("meu_portfolio.meus_serviços.title")}
          </Text>
        </Flex>
        {isLargerThan991 ? (
          <Tabs
            value={activeTab}
            onTabChange={setActiveTab}
            radius="xs"
            defaultValue={contentTabs[0].value}
            styles={{
              root: {
                zIndex: 1,
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "::-webkit-scrollbar": {
                  display: "none",
                },
              },
              tabsList: {
                borderBottom: `1px solid ${theme.colors.basic[2]}`,
                flexWrap: "nowrap",
              },
            }}
          >
            <Tabs.List>
              {contentTabs.map((tabItem, index) => (
                <Tabs.Tab
                  value={tabItem?.value}
                  key={`${index}-${tabItem?.label}`}
                  onClick={() => handleTabChange(tabItem.value)}
                  disabled={tabItem?.badgeNumber === 0}
                  rightSection={
                    <Badge
                      color={tabItem?.color}
                      h={22}
                      styles={{
                        root: {
                          fontWeight: 600,
                          fontSize: "14px",
                        },
                      }}
                      variant="filled"
                      px={8}
                    >
                      {tabItem?.badgeNumber >= 10
                        ? tabItem?.badgeNumber
                        : "0" + tabItem?.badgeNumber}
                    </Badge>
                  }
                >
                  {tabItem?.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        ) : (
          <Select
            label={t("meu_portfolio.minhas_compras.select.label")}
            placeholder={t("meu_portfolio.minhas_compras.select.label")}
            data={contentTabs}
            itemComponent={SelectItem}
            value={query.active}
            id="select"
            styles={{
              icon: {
                width: "unset",
              },
              input: {
                paddingLeft: "64px !important",
              },
            }}
            icon={
              <Badge
                color={
                  contentTabs.find((tab) => tab.value === query.active)?.color
                }
                h={22}
                styles={{
                  root: {
                    fontWeight: 600,
                    fontSize: "14px",
                  },
                }}
                variant="filled"
                px={8}
              >
                {badgeNumber && badgeNumber >= 10
                  ? badgeNumber
                  : "0" + badgeNumber}
              </Badge>
            }
            onChange={(value) => {
              const newValue = value as EServiceType | "";
              handleTabChange(newValue);
            }}
          />
        )}
        {isLoading ? (
          <Center mt="32px">
            <Loader />
          </Center>
        ) : !!data && data?.data?.length > 0 ? (
          <Flex direction={"column"} gap={16} my="24px">
            {data?.data.map((myProduct, index) => (
              <Flex
                key={`${index}-${myProduct?.id}`}
                direction="column"
                w={"100%"}
                bg="#fff"
                sx={{
                  boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.05)",
                  borderRadius: "12px",
                }}
              >
                <CardShopping
                  myProduct={myProduct}
                  key={`${index}-${myProduct?.id}`}
                />
              </Flex>
            ))}
          </Flex>
        ) : (
          <Center mt="32px">
            <Text
              fz={{ base: 26 }}
              fw={{ base: 700 }}
              c="basic.5"
              lh={{ base: "155%" }}
            >
              {t("global.pagination.no_data")}
            </Text>
          </Center>
        )}
      </Flex>
    </Center>
  );
};

export default MyProducts;
